/**
 * Build TSC/TSPL command bytes for direct RAW label printing (desktop Electron only).
 * Matches BarcodeGeneratePage field positions in mm; coordinates include label margins.
 *
 * v1: single column (roll / one label per print). Multi-column sheet → use browser print.
 */

const MM_PER_IN = 25.4;

/** TSC built-in bitmap fonts: dot width × height per character (before x/y multiplier). */
const FONT_METRICS = {
  '1': { w: 8, h: 12 },
  '2': { w: 12, h: 20 },
  '3': { w: 16, h: 24 },
  '4': { w: 24, h: 32 },
  '5': { w: 32, h: 48 },
};

/** UTF-8 bytes; works in browsers and Node/Jest (no TextEncoder in older Node test env). */
function encodeUtf8(string) {
  if (typeof TextEncoder !== 'undefined') {
    return new TextEncoder().encode(string);
  }
  if (typeof Buffer !== 'undefined') {
    return new Uint8Array(Buffer.from(string, 'utf8'));
  }
  throw new Error('UTF-8 encoding is not available');
}

function normalizeDpi(d) {
  return Number(d) === 300 ? 300 : 203;
}

/**
 * @param {number} mm
 * @param {number} dpi
 * @returns {number} dot position (integer)
 */
export function mmToDots(mm, dpi) {
  return Math.max(0, Math.round((Number(mm) * dpi) / MM_PER_IN));
}

/**
 * @param {string} s
 * @returns {string}
 */
function escapeTsplData(s) {
  if (s == null) return '';
  return String(s).replace(/"/g, "'").replace(/\r\n|\r|\n/g, ' ').replace(/\0/g, '');
}

/**
 * Match BarcodeGeneratePage preview: itemFontPx = min(itemFontPt * 1.2, labelHmm * 3.78 * 0.8).
 * Convert that cap to TSPL font id + multiplier so thermal output matches the on-screen label.
 *
 * @param {number} fontPt
 * @param {number} labelHeightMm
 * @param {number} [itemHeightMm]
 * @param {number} dpi
 * @returns {{ font: string, xMul: number, yMul: number, charHDots: number }}
 */
export function resolveTsplFont(fontPt, labelHeightMm, itemHeightMm, dpi) {
  const pt = Number(fontPt);
  const safePt = Number.isFinite(pt) && pt > 0 ? pt : 8;
  const labelHmm = Math.max(1, Number(labelHeightMm) || 35);
  const boxHmm = Math.max(1, Number(itemHeightMm) || 15);

  const previewPxCap = labelHmm * 3.78 * 0.8;
  const previewPx = Math.min(safePt * 1.2, previewPxCap);
  const targetHDots = Math.max(
    FONT_METRICS['2'].h,
    Math.min(mmToDots(boxHmm, dpi), mmToDots(labelHmm * 0.85, dpi), Math.round((previewPx / 3.78) * dpi / MM_PER_IN))
  );

  const candidates = [
    { font: '2', xMul: 1 },
    { font: '2', xMul: 2 },
    { font: '3', xMul: 1 },
    { font: '3', xMul: 2 },
    { font: '4', xMul: 1 },
  ];

  let best = candidates[0];
  let bestDiff = Infinity;
  for (const c of candidates) {
    const h = FONT_METRICS[c.font].h * c.xMul;
    const diff = Math.abs(h - targetHDots);
    if (h <= targetHDots + 2 && diff < bestDiff) {
      bestDiff = diff;
      best = c;
    }
  }

  if (FONT_METRICS[best.font].h * best.xMul > targetHDots + 4) {
    best = { font: '2', xMul: 1 };
  }

  return {
    font: best.font,
    xMul: best.xMul,
    yMul: best.xMul,
    charHDots: FONT_METRICS[best.font].h * best.xMul,
  };
}

/**
 * @param {string} text
 * @param {string} fontId
 * @param {number} xMul
 * @returns {number}
 */
function estimateTextWidthDots(text, fontId, xMul) {
  const base = FONT_METRICS[fontId]?.w || 12;
  return Math.max(0, Math.round(String(text).length * base * xMul));
}

/**
 * @param {object} item field layout item
 * @param {string} text
 * @param {string} align left|centre|center|right
 * @param {number} mLeftDot
 * @param {number} mTopDot
 * @param {number} dpi
 * @param {string} fontId
 * @param {number} xMul
 * @returns {{ x: number, y: number }}
 */
function textOriginDots(item, text, align, mLeftDot, mTopDot, dpi, fontId, xMul) {
  const leftMm = item.left != null ? Number(item.left) : 4;
  const topMm = item.top != null ? Number(item.top) : 2;
  const widthMm = item.width != null ? Number(item.width) : 35;

  const leftDot = mLeftDot + mmToDots(leftMm, dpi);
  const topDot = mTopDot + mmToDots(topMm, dpi);
  const boxWd = mmToDots(widthMm, dpi);

  const a = (align || 'left').toLowerCase();
  const est = estimateTextWidthDots(text, fontId, xMul);
  if (a === 'right') {
    return { x: Math.max(0, leftDot + boxWd - est), y: topDot };
  }
  if (a === 'centre' || a === 'center') {
    return { x: Math.max(0, leftDot + Math.floor((boxWd - est) / 2)), y: topDot };
  }
  return { x: leftDot, y: topDot };
}

/**
 * QR module cell width so the symbol fits the configured mm box.
 * @param {number} sideDots
 * @param {number} dataLen
 * @returns {number}
 */
function qrCellWidthForBox(sideDots, dataLen) {
  const len = Math.max(1, dataLen);
  const modules = len <= 12 ? 25 : len <= 20 ? 29 : 33;
  const cell = Math.floor(sideDots / (modules + 8));
  return Math.max(2, Math.min(10, cell));
}

/**
 * One label: CLS, elements, PRINT 1,1.
 * @param {object} p
 * @param {object} p.settings label settings (same shape as BarcodeGeneratePage)
 * @param {Array<object>} p.fieldLayout
 * @param {object} p.product
 * @param {function} p.getFieldValue
 * @param {number} p.labelDpi
 * @returns {string[]}
 */
function buildOneLabelCommands(p) {
  const { settings, fieldLayout, product, getFieldValue, labelDpi: dpi } = p;
  const codeType = (settings.codeType || 'BARCODE').toUpperCase();
  const mLeft = Number(settings.marginLeftMm) || 0;
  const mTop = Number(settings.marginTopMm) || 0;
  const mLeftDot = mmToDots(mLeft, dpi);
  const mTopDot = mmToDots(mTop, dpi);
  const labelHeightMm = Math.max(1, Number(settings.labelHeightMm) || 35);
  const baseFontPt = settings.fontSizePt ?? 8;
  const barcodeW = Math.max(1, Number(settings.barcodeWidthMm) || 25);
  const barcodeH = Math.max(1, Number(settings.barcodeHeightMm) || 15);

  const lines = ['CLS'];
  const qrField = fieldLayout.find((f) => f.fieldKey === 'QR' && f.show !== false);
  const showCodeImage =
    (codeType === 'QR' || codeType === 'BARCODE') && !!qrField;

  const dataStr = escapeTsplData(product.barcode != null && product.barcode !== '' ? String(product.barcode) : String(product.id));

  if (showCodeImage && dataStr) {
    const ql = Number(qrField?.left) || 0;
    const qt = Number(qrField?.top) || 0;
    const qw = qrField?.width != null ? Number(qrField.width) : barcodeW;
    const qh = qrField?.height != null ? Number(qrField.height) : barcodeH;
    const x = mLeftDot + mmToDots(ql, dpi);
    const y = mTopDot + mmToDots(qt, dpi);
    if (codeType === 'QR') {
      const side = Math.max(mmToDots(Math.min(qw, qh), dpi), 24);
      const cell = qrCellWidthForBox(side, dataStr.length);
      lines.push(`QRCODE ${x},${y},M,${cell},A,0,"${dataStr}"`);
    } else {
      const h = Math.max(20, Math.min(320, mmToDots(qh, dpi)));
      const narrow = 2;
      const wide = h > 120 ? 4 : 3;
      lines.push(`BARCODE ${x},${y},"128",${h},0,0,${narrow},${wide},"${dataStr}"`);
    }
  }

  const textItems = fieldLayout.filter((f) => f.fieldKey !== 'QR' && f.show !== false);
  let lastTextBottomDot = 0;
  const minFieldGapDot = mmToDots(0.5, dpi);

  for (const item of textItems) {
    const key = item.fieldKey;
    const text = getFieldValue(product, key);
    if (text == null || String(text).trim() === '') continue;

    const itemFontPt = item.fontSize ?? baseFontPt;
    const itemHeightMm = item.height != null ? Number(item.height) : 15;
    const align = (item.alignment || 'Left').toLowerCase() === 'centre' || (item.alignment || '').toLowerCase() === 'center'
      ? 'centre'
      : (item.alignment || 'Left').toLowerCase() === 'right'
        ? 'right'
        : 'left';

    if (key === 'MRP' || key === 'mrp') {
      const topBase = item.top != null ? Number(item.top) : 2;
      const headerPt = Math.max(6, Math.round(Number(itemFontPt) * 0.9));
      const headerFont = resolveTsplFont(headerPt, labelHeightMm, Math.max(4, itemHeightMm * 0.4), dpi);
      const valueFont = resolveTsplFont(itemFontPt, labelHeightMm, itemHeightMm, dpi);

      let vM = textOriginDots(
        { ...item, top: topBase },
        'MRP',
        align,
        mLeftDot,
        mTopDot,
        dpi,
        headerFont.font,
        headerFont.xMul
      );
      if (vM.y < lastTextBottomDot + minFieldGapDot) {
        vM = { ...vM, y: lastTextBottomDot + minFieldGapDot };
      }

      const valueTopMm = topBase + (headerFont.charHDots / dpi) * MM_PER_IN + 0.5;
      let vVal = textOriginDots(
        { ...item, top: valueTopMm },
        String(text),
        align,
        mLeftDot,
        mTopDot,
        dpi,
        valueFont.font,
        valueFont.xMul
      );
      if (vVal.y < vM.y + headerFont.charHDots + minFieldGapDot) {
        vVal = { ...vVal, y: vM.y + headerFont.charHDots + minFieldGapDot };
      }

      lines.push(`TEXT ${vM.x},${vM.y},"${headerFont.font}",0,${headerFont.xMul},${headerFont.yMul},"MRP"`);
      lines.push(
        `TEXT ${vVal.x},${vVal.y},"${valueFont.font}",0,${valueFont.xMul},${valueFont.yMul},"${escapeTsplData(text)}"`
      );
      lastTextBottomDot = vVal.y + valueFont.charHDots;
      continue;
    }

    const fontSpec = resolveTsplFont(itemFontPt, labelHeightMm, itemHeightMm, dpi);
    let { x, y } = textOriginDots(item, String(text), align, mLeftDot, mTopDot, dpi, fontSpec.font, fontSpec.xMul);
    if (y < lastTextBottomDot + minFieldGapDot) {
      y = lastTextBottomDot + minFieldGapDot;
    }
    lines.push(`TEXT ${x},${y},"${fontSpec.font}",0,${fontSpec.xMul},${fontSpec.yMul},"${escapeTsplData(text)}"`);
    lastTextBottomDot = y + fontSpec.charHDots;
  }

  lines.push('PRINT 1,1');
  return lines;
}

/**
 * @param {object} params
 * @param {object} params.settings
 * @param {number} [params.labelDpi]
 * @param {object[]} params.products
 * @param {function(object, string): string} params.getFieldValue
 * @param {object[]} params.fieldLayout - visible fields, sorted (same as getFieldLayout() on the page)
 * @returns {{ ok: true, buffer: Uint8Array } | { ok: false, reason: string }}
 */
export function buildTsplForLabels({ settings, labelDpi, products, getFieldValue, fieldLayout }) {
  if (!Array.isArray(products) || products.length === 0) {
    return { ok: false, reason: 'No labels to print' };
  }
  if (typeof getFieldValue !== 'function') {
    return { ok: false, reason: 'getFieldValue is required' };
  }
  if (!Array.isArray(fieldLayout)) {
    return { ok: false, reason: 'fieldLayout is required' };
  }

  const cols = Math.max(1, parseInt(settings.noOfColumns, 10) || 1);
  if (cols !== 1) {
    return { ok: false, reason: 'TSPL direct print supports single-column (roll) layout only' };
  }

  const w = Math.max(1, Number(settings.labelWidthMm) || 39);
  const h = Math.max(1, Number(settings.labelHeightMm) || 35);
  const dpi = normalizeDpi(labelDpi);
  const gapMm = Math.max(0, Number(settings.verticalGapMm) || 0);
  const gap = gapMm > 0 ? gapMm : 2;

  const header = [
    `SIZE ${w} mm, ${h} mm`,
    `GAP ${gap} mm, 0`,
    'DIRECTION 1',
    'REFERENCE 0,0',
    'SPEED 4',
    'DENSITY 10',
    'SET TEAR ON',
  ];

  const all = [];
  for (const part of header) {
    all.push(part);
  }

  for (const product of products) {
    const one = buildOneLabelCommands({
      settings,
      fieldLayout,
      product,
      getFieldValue,
      labelDpi: dpi,
    });
    for (const line of one) {
      all.push(line);
    }
  }

  const body = all.join('\r\n') + '\r\n';
  const buffer = encodeUtf8(body);
  return { ok: true, buffer };
}

/**
 * @param {object} settings
 * @returns {boolean}
 */
export function isTsplLayoutSupported(settings) {
  const cols = Math.max(1, parseInt(settings?.noOfColumns, 10) || 1);
  return cols === 1;
}
